<template>
  <v-card
    flat
    class="ml-4 d-flex flex-column align-center justify-center rounded-20"
    height="100%"
  >
    <v-card-title>
      <div class="mx-auto">
        <div class="text-center mb-2">
          <v-progress-circular indeterminate size="64" v-if="loading" />
          <v-icon
            size="64"
            color="success"
            v-if="
              associationPass &&
              !libraryAssociationCollisions.length &&
              !loading
            "
          >
            mdi-check-circle
          </v-icon>
          <v-icon
            size="64"
            color="warning"
            v-if="
              (!associationPass || libraryAssociationCollisions.length) &&
              !loading
            "
          >
            mdi-alert-circle-outline
          </v-icon>
        </div>
        <div>
          Scan Gauge Association
          <span v-if="associationPass && !loading"> Completed </span>
          <span v-if="!associationPass && !loading">
            Warning{{
              !associationPass && libraryAssociationCollisions.length
                ? "s"
                : null
            }}
          </span>
        </div>
      </div>
    </v-card-title>
    <v-card-text
      v-if="
        (!associationPass || libraryAssociationCollisions.length) && !loading
      "
      class="text-center"
    >
      <template v-if="!associationPass">
        <h3 class="black--text mb-4">Alignment Warning</h3>
        <p>
          Please manually check the gauges highlighted in orange to ensure that
          the scan bodies are positioned correctly. This is a warning only.
          <br />
          <strong>The case may continue at the labs discretion.</strong>
        </p>
      </template>
      <template v-if="libraryAssociationCollisions.length">
        <h3 class="black--text mb-4">Proximity Warning</h3>
        <p>
          If Scan Gauges are touching, one may not be seated, this can create
          inaccurate scan exports. This is a warning only, and may not be true
          to the actual scan data.
          <br />
          <strong>The case may continue at the labs discretion.</strong>
        </p>
        <p class="font-italic">
          Proximity cannot be determined at the analyzer phase, only when the
          library is associated.
        </p>
        <p>
          If you believe the error is due to a mis-selected Scan Gauge during
          library association, click 'Redo Association'. If not, new scans will
          be needed, OR, continue with the case as is.
        </p>
      </template>
    </v-card-text>
    <v-card flat v-if="libraryAssociationResponse || showFailure">
      <v-card-text>
        <v-row>
          <v-col class="ml-10">
            <div></div>
            <v-switch
              :disabled="loading"
              inset
              dense
              hide-details
              label="Scan Gauge Libraries"
              v-model="associationViewOptions.showLibraries"
              class="mt-2"
            />
            <v-switch
              :disabled="loading"
              inset
              dense
              hide-details
              label="Constructed Libraries"
              v-model="associationViewOptions.showConstructedLibraries"
              class="mt-2"
            />
          </v-col>
          <v-col
            :class="{
              'mr-10': true,
              'ml-10': $vuetify.breakpoint.lgAndDown
            }"
          >
            <v-switch
              :disabled="loading"
              inset
              dense
              hide-details
              label="Scan Gauges"
              v-model="associationViewOptions.showScanGauges"
              class="mt-2"
            />
            <v-switch
              :disabled="loading"
              inset
              dense
              hide-details
              label="Tissue Scan"
              v-model="associationViewOptions.showScanTissue"
              class="mt-2"
            />
          </v-col>
        </v-row>
        <AssociateToCase v-model="associateToCase" />
      </v-card-text>
    </v-card>
    <v-card-actions>
      <v-btn text class="btn-primary mx-auto" @click="redoAssociation">
        <v-icon class="mr-2">mdi-restore</v-icon>
        Redo Association
      </v-btn>
      <v-btn text class="btn-primary ml-3" @click="$emit('next')">
        <v-icon class="mr-2">mdi-download</v-icon>
        Download Export
      </v-btn>
      <!-- <v-btn
        v-if="associationPass"
        small
        text
        class="btn-primary ml-3"
        @click="associateToCase = true"
      >
        <v-icon class="mr-2">mdi-link-box-outline</v-icon>
        Associate to Case
      </v-btn> -->
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import AssociateToCase from "../AssociateToCase.vue"

// import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"

export default {
  name: "PostAssociation",

  components: {
    AssociateToCase
  },

  data() {
    return {
      loading: false,
      associationViewOptions: {
        showScanGauges: false,
        showScanTissue: true,
        showLibraries: false,
        showConstructedLibraries: true
      },
      associateToCase: false
    }
  },

  watch: {
    associationViewOptions: {
      handler(val) {
        this.setLibraryAssociationViewOptions(val)
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters(["libraryAssociationResponse", "selectedGaugeLibraries"]),

    libraryAssociationMetrics() {
      return this.libraryAssociationResponse?.librariesCombinedScanComparison.libraryComparisons.map(
        (obj) => {
          return { value: obj.percentiles[2], pass: obj.passed }
        }
      )
    },

    associationPass() {
      const metricsPass = this.libraryAssociationMetrics?.every((m) => m?.pass)
      // const collisions = this.libraryAssociationCollisions?.length
      return metricsPass
      // && !collisions
    },

    libraryAssociationCollisions() {
      return (
        this.libraryAssociationResponse?.librariesCombinedScanComparison
          ?.collisions || []
      )
    }
  },

  methods: {
    ...mapActions([
      "setLibraryAssociationViewOptions",
      "setLibraryAssociationEnabled",
      "setSelectedGaugeLibraries",
      "setLibraryAssociationResponse"
    ]),

    redoAssociation() {
      this.loading = true

      if (this.showFailure) this.showFailure = false

      this.setSelectedGaugeLibraries([])
      this.setLibraryAssociationViewOptions({
        showScanGauges: true,
        showScanTissue: true,
        showLibraries: false,
        showConstructedLibraries: false
      })
      this.setLibraryAssociationResponse(null)
      this.setLibraryAssociationEnabled(true)
      this.loading = false
    }
  },

  mounted() {
    this.setLibraryAssociationViewOptions(this.associationViewOptions)

    if (!this.associationPass) this.showFailure = true
  }
}
</script>
